import React, { useEffect, useId, useRef, useState } from 'react';
import { BROKER_BRAND, resolveBrokerKey } from './brokerBranding';
import { brokerAccountDisplayName } from './clientReportingUtils';
import { IconChevronDown, IconCheck } from '../Icons';

/**
 * Switch portfolios from the detail page title.
 *
 * Getting to another account used to mean going back to the dashboard and
 * opening the one you wanted, which is three interactions to answer "how does
 * my other account look?". The title already names the current portfolio, so it
 * is the natural place to change it.
 *
 * Renders as plain text when there is nothing to switch to — a dropdown arrow
 * that opens a list of one is a promise the UI cannot keep.
 */

function OptionBadge({ client }) {
  const key = resolveBrokerKey(client?.name, client?.broker);
  const brand = key ? BROKER_BRAND[key] : null;
  if (brand?.logoSrc) {
    return (
      <span className={`vd-broker-logo ${brand.tone} has-image sm`} aria-hidden="true">
        <img src={brand.logoSrc} alt="" />
      </span>
    );
  }
  return (
    <span className="vd-broker-logo sm" aria-hidden="true">
      {brand?.shortName || (client?.broker || '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

export default function ClientReportingPortfolioSwitcher({
  client,
  clients = [],
  onSelect,
  title,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    }
    function handleEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const label = title || brokerAccountDisplayName(client);
  const canSwitch = typeof onSelect === 'function' && clients.length > 1;

  if (!canSwitch) {
    return <h2 className="vd-ml-detail-title">{label}</h2>;
  }

  return (
    <div className={`vd-cr-portfolio-switcher${open ? ' open' : ''}`} ref={rootRef}>
      {/* The heading wraps the button, not the other way round: a <button>
          accepts phrasing content only, so an <h2> inside it is invalid and
          costs the heading semantics screen readers navigate by. */}
      <h2 className="vd-ml-detail-title vd-cr-portfolio-switcher-title">
        <button
          type="button"
          className="vd-cr-portfolio-switcher-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((prev) => !prev)}
        >
          {label}
          <IconChevronDown size={18} className="vd-cr-portfolio-switcher-chevron" />
        </button>
      </h2>

      {open ? (
        <div id={listId} className="vd-cr-portfolio-switcher-menu" role="listbox">
          {clients.map((option) => {
            const checked = option.id === client?.id;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={checked}
                className={`vd-cr-portfolio-switcher-row${checked ? ' active' : ''}`}
                onClick={() => { setOpen(false); if (!checked) onSelect(option.id); }}
              >
                <OptionBadge client={option} />
                <span className="vd-cr-portfolio-switcher-name">
                  {brokerAccountDisplayName(option)}
                </span>
                {checked ? <IconCheck size={14} className="vd-select-option-check" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
